<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import { todayPrice } from '@/domain/menu-import/live-update';
  import {
    liveShapesDiffer,
    liveSizeToday,
  } from '@/domain/menu-import/live-rows';
  import type {
    IPriceSizesErrors,
    IPriceSizesForm,
  } from '@/domain/menu-import/item-editor';
  import { formatMoney } from '@/domain/store-setup/money';
  import type { IDraftItem } from '@/interfaces/IMenuImport';
  import V2MenuSizesEditor from '../editor/V2MenuSizesEditor.vue';

  /**
   * The prices of a dish already on the menu (plan §3.5 «Editor of a linked
   * dish»), shared by its editor and its review-queue card: the price or
   * the size prices only (size names fixed), each with «Hoy: {price}». When
   * the photo and the menu disagree on having sizes, nothing can be typed:
   * today's prices and why. Controlled like `V2MenuSizesEditor`.
   */
  const props = withDefaults(
    defineProps<{
      /** A linked dish (`live.dish` set). */
      item: IDraftItem;
      value: IPriceSizesForm;
      errors?: IPriceSizesErrors | null;
      currency: string;
      country: string;
    }>(),
    { errors: null }
  );
  const emit = defineEmits<{
    (e: 'input', value: IPriceSizesForm): void;
    (e: 'focus-change', inside: boolean): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;

  function money(value: number): string {
    return formatMoney(value, props.currency, props.country);
  }

  function todayText(value: number): string {
    return String(proxy.$t('menuImport.update.today', { price: money(value) }));
  }

  const dish = computed(() => props.item.live?.dish ?? null);
  const mismatch = computed(() => liveShapesDiffer(props.item));
  const priceHint = computed(() =>
    dish.value ? todayText(todayPrice(dish.value)) : null
  );
  const hints = computed(() =>
    liveSizeToday(props.item).map((price) =>
      price === null ? null : todayText(price)
    )
  );
  /** Today's prices as the menu has them (the shapes disagree). */
  const todayLines = computed(() => {
    const current = dish.value;
    if (!current) return [];
    return current.sizes.length >= 2
      ? current.sizes.map((size) => `${size.label} ${money(size.price)}`)
      : [money(todayPrice(current))];
  });
</script>

<template>
  <div v-if="dish && mismatch" class="v2-menu-live-price-fields is-fixed">
    <p class="fixed-label">{{ $t('menuImport.editor.price') }}</p>
    <ul class="fixed-list">
      <li v-for="line in todayLines" :key="line" class="fixed-line">
        {{ line }}
      </li>
    </ul>
    <p class="fixed-note">{{ $t('menuImport.update.sizesMismatch') }}</p>
  </div>
  <V2MenuSizesEditor
    v-else
    class="v2-menu-live-price-fields"
    :value="value"
    :errors="errors"
    :currency="currency"
    fixed-labels
    :price-hint="priceHint"
    :hints="hints"
    @input="emit('input', $event)"
    @focus-change="emit('focus-change', $event)"
  />
</template>

<style scoped lang="scss">
  .v2-menu-live-price-fields.is-fixed {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5;

    .fixed-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .fixed-list {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-p-0;
    }
    .fixed-line {
      @apply tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
    }
    .fixed-note {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
  }
</style>
